import { FormInstance } from 'antd';
import dotPropImmutable from 'dot-prop-immutable';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { AccountInitialData } from 'api/account';
import { User, UserType } from 'api/models';
import { toastService } from 'components/molecules/toast';
import { inviteMembersAction, updateMemberAction } from 'data/members/actions';
import { Member } from 'data/members/api';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { ACCOUNT_META_DATA_QUERY_KEY } from 'queries/account';
import { ALL_MEMBERS_QUERY_KEY } from 'queries/members';
import { ROLES_QUERY_KEY } from 'queries/roles';
import { logger } from 'services';

export const useInviteModalActions = ({
  form,
  onClose,
  onUserInvited,
  showMemberDetail,
}: {
  form: FormInstance;
  onClose(): void;
  onUserInvited(): void;
  showMemberDetail?(member: Member): void;
}): {
  isSaving: boolean;
  onInvite(): void;
  onUpdate(user: User): void;
} => {
  const [isSaving, setIsSaving] = useState(false);
  const { user: currentUser, company } = useUser();
  const dispatch = useDispatch();
  const intl = useIntl();
  const successTitle = useTranslation('success');
  const errorTitle = useTranslation('failure');
  const partialTitle = useTranslation(
    'settings.team_members.invite.partial.title',
  );
  const queryClient = useQueryClient();

  const onInvite = () => {
    form
      .validateFields()
      .then(
        ({
          email,
          role,
          permission,
          timeSheetApprover,
          defaultCapacity,
          capacity,
          fields,
        }) => {
          setIsSaving(true);
          const formData = email.map((emailObj: { value: string }) => {
            return {
              user: {
                emailId: emailObj.value,
                userType: UserType.Native,
                company: {
                  companyId: company.companyId,
                },
                role: {
                  roleId: role.value,
                },
                fields: fields ?? [],
                defaultCapacity,
                capacity,
                timeSheetApprover: timeSheetApprover.value ?? -1,
              },
              permissionSet: {
                permissionSetType: permission.value,
              },
            };
          });

          dispatch(
            inviteMembersAction({
              data: formData,
              role: {
                value: role.value,
                isNew: !!role.__isNew__,
              },
              async handleSuccess() {
                form.resetFields();

                onClose();

                toastService({
                  title: successTitle,
                  message: intl.formatMessage(
                    {
                      id: 'settings.team_members.invite.success',
                    },
                    {
                      number: formData.length,
                    },
                  ),
                }).success();

                onUserInvited();

                await queryClient.invalidateQueries('roles');
                await queryClient.invalidateQueries(ROLES_QUERY_KEY);
                await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
              },
              handleFailure(data: Member[]) {
                if (isEmpty(data)) {
                  toastService({
                    title: errorTitle,
                    message: intl.formatMessage({
                      id: 'settings.team_members.invite.error',
                    }),
                  }).error();
                } else {
                  const emailsText = data
                    .slice(0, 2)
                    .map(({ user }) => user.emailId)
                    .join(', ');
                  const countText =
                    data.length > 2
                      ? '+'.concat(String(data.length - 2))
                      : undefined;

                  toastService({
                    title: partialTitle,
                    message: `${emailsText} ${countText ?? ''}`,
                  }).error();

                  form.resetFields();
                  onClose();
                  onUserInvited();
                  queryClient.invalidateQueries('roles');
                  queryClient.invalidateQueries(ROLES_QUERY_KEY);
                }
              },
              handleAlways() {
                setIsSaving(false);
              },
            }),
          );
        },
      )
      .catch((error) => {
        logger.error('invite failed', { error });
      });
  };

  const onUpdate = (user: User) => {
    form
      .validateFields()
      .then(
        ({
          role,
          permission,
          timeSheetApprover,
          defaultCapacity,
          capacity,
          fields,
        }) => {
          setIsSaving(true);
          const formData = {
            user: {
              userId: user.userId,
              role: {
                roleId: role.value,
              },
              fields,
              defaultCapacity,
              capacity,
              timeSheetApprover: timeSheetApprover.value ?? -1,
            },
            permissionSet: {
              permissionSetType: permission.value,
            },
          };

          dispatch(
            updateMemberAction({
              data: [formData],
              role: {
                value: role.value,
                isNew: !!role.__isNew__,
              },
              async handleSuccess() {
                await queryClient.invalidateQueries('roles');
                await queryClient.invalidateQueries(ROLES_QUERY_KEY);
                await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);

                const all_members = queryClient.getQueryData<any>(
                  ALL_MEMBERS_QUERY_KEY,
                );
                const updatedMember = all_members?.members?.find(
                  (member) => member.user.userId === user.userId,
                );

                showMemberDetail?.(updatedMember);
                await toastService({
                  title: successTitle,
                  message: intl.formatMessage({
                    id: 'settings.team_members.edit.success',
                  }),
                }).success();

                onUserInvited();

                setIsSaving(false);

                if (user.userId === currentUser.userId) {
                  queryClient.setQueryData<AccountInitialData>(
                    ACCOUNT_META_DATA_QUERY_KEY,
                    (oldValues) =>
                      dotPropImmutable.merge(oldValues, 'userProfile.user', {
                        role: {
                          roleId: role.value,
                          roleName: role.label,
                        },
                        fields,
                        defaultCapacity,
                        capacity,
                        timeSheetApprover,
                      }),
                  );
                }
              },
              handleFailure() {
                toastService({
                  title: errorTitle,
                  message: intl.formatMessage({
                    id: 'settings.team_members.edit.error',
                  }),
                }).error();
                setIsSaving(false);
              },
            }),
          );
        },
      );
  };

  return {
    isSaving,
    onInvite,
    onUpdate,
  };
};
